import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar, Loader2, Check, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

const EVENT_TYPES = [
  { value: 'general', label: 'General Event', color: 'bg-slate-100 text-slate-700' },
  { value: 'medical_appointment', label: 'Medical Appointment', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'birthday', label: 'Birthday', color: 'bg-pink-100 text-pink-700' },
  { value: 'anniversary', label: 'Anniversary', color: 'bg-rose-100 text-rose-700' },
  { value: 'reservation', label: 'Reservation', color: 'bg-blue-100 text-blue-700' },
  { value: 'car_maintenance', label: 'Car Maintenance', color: 'bg-orange-100 text-orange-700' },
  { value: 'membership', label: 'Membership/Renewal', color: 'bg-purple-100 text-purple-700' },
  { value: 'family', label: 'Family Event', color: 'bg-green-100 text-green-700' },
];

const CalendarAddEventModal = ({ isOpen, onClose, selectedDate, onEventAdded }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    event_type: 'general',
    date: '',
    time: '',
    location: '',
    notes: '',
    is_annual: false,
  });

  // Reset form and set date when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        title: '',
        event_type: 'general',
        date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
        time: '',
        location: '',
        notes: '',
        is_annual: false,
      });
    }
  }, [isOpen, selectedDate]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Please enter an event title');
      return;
    }
    
    if (!formData.date) {
      toast.error('Please select a date');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await API.post('/calendar/events/manual', {
        title: formData.title.trim(),
        event_type: formData.event_type,
        date: formData.date,
        time: formData.time || null,
        location: formData.location.trim() || null,
        notes: formData.notes.trim() || null,
        is_annual: formData.is_annual,
      });
      
      const result = response.data;
      
      toast.success(result.message || 'Event created successfully', {
        description: `Scheduled for ${formatDisplayDate(formData.date)}`,
        duration: 3000,
      });
      
      // Trigger refresh callback
      if (onEventAdded) {
        onEventAdded(result);
      }
      
      onClose();
      
    } catch (error) {
      console.error('Failed to create event:', error);
      toast.error('Failed to create event', {
        description: error.response?.data?.detail || 'Please try again',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDisplayDate = (dateStr) => {
    try {
      const date = new Date(dateStr + 'T00:00:00');
      return date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const selectedEventType = EVENT_TYPES.find(t => t.value === formData.event_type);

  // Should show annual toggle for certain event types
  const showAnnualToggle = ['birthday', 'anniversary', 'membership'].includes(formData.event_type);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-white" data-testid="add-event-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-800">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
              <Calendar className="h-4 w-4 text-blue-600" />
            </div>
            Add New Event
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title" className="text-slate-700">Event Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Enter event title"
              className="border-slate-200 focus:border-blue-400"
              data-testid="event-title-input"
            />
          </div>
          
          {/* Event Type */}
          <div className="space-y-2">
            <Label className="text-slate-700">Event Type</Label>
            <Select 
              value={formData.event_type}
              onValueChange={(value) => handleChange('event_type', value)}
            >
              <SelectTrigger 
                className="border-slate-200"
                data-testid="event-type-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EVENT_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      <span className={cn("px-2 py-0.5 rounded text-xs font-medium", type.color)}>
                        {type.label}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedEventType && selectedEventType.value !== 'general' && (
              <p className="text-xs text-slate-500 flex items-center gap-1">
                <Sparkles className="h-3 w-3" />
                This will also create a record in the corresponding module
              </p>
            )}
          </div>
          
          {/* Date and Time Row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="date" className="text-slate-700">Date *</Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                className="border-slate-200 focus:border-blue-400"
                data-testid="event-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="time" className="text-slate-700">Time (optional)</Label>
              <Input
                id="time"
                type="time"
                value={formData.time}
                onChange={(e) => handleChange('time', e.target.value)}
                className="border-slate-200 focus:border-blue-400"
                data-testid="event-time-input"
              />
            </div>
          </div>
          
          {/* Annual Toggle for Birthdays, Anniversaries, etc. */}
          {showAnnualToggle && (
            <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg">
              <input
                type="checkbox"
                id="is_annual"
                checked={formData.is_annual}
                onChange={(e) => handleChange('is_annual', e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                data-testid="event-annual-checkbox"
              />
              <Label htmlFor="is_annual" className="text-sm text-slate-600 cursor-pointer">
                Repeats annually (for birthdays, anniversaries, etc.)
              </Label>
            </div>
          )}
          
          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location" className="text-slate-700">Location (optional)</Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => handleChange('location', e.target.value)}
              placeholder="Enter location"
              className="border-slate-200 focus:border-blue-400"
              data-testid="event-location-input"
            />
          </div>
          
          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-slate-700">Notes (optional)</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Add any additional notes..."
              className="border-slate-200 focus:border-blue-400 min-h-[80px] resize-none"
              data-testid="event-notes-input"
            />
          </div>
          
          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-slate-200"
              data-testid="cancel-event-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !formData.title.trim()}
              className={cn(
                "bg-blue-500 hover:bg-blue-600 text-white",
                "disabled:bg-slate-200 disabled:text-slate-400"
              )}
              data-testid="save-event-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Create Event
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CalendarAddEventModal;
